import { ThemeConfig } from "@/config/theme/themeTypes";

/**
 * Inyecta scripts de Google Tag Manager y Hotjar al <head> usando las keys del
 * theme actual. Reemplaza la carga estatica que antes vivia hardcoded en
 * public/index.html.
 *
 * Idempotente: si un script ya fue inyectado, no se duplica. Los marcadores
 * usan IDs explicitos (`rc-script-gtm`, etc).
 *
 * Llamar UNA SOLA VEZ tras themeService.applyRemote() en main.ts.
 *
 * Comportamiento (post Fase 45 + 46):
 *  - El tenant carga sus propias keys (GTM/Ads ID + Hotjar).
 *  - Si el tenant NO setea una key (campo null/ausente en /api/tenant/theme),
 *    el script correspondiente NO se inyecta. Las integraciones son opt-in
 *    per tenant, no hay fallback a keys "globales".
 *  - Sin theme remoto (fallback en .catch): los defaults de defaultThemeConfig
 *    son null para todas las integraciones → ningun script se carga. Solo los
 *    colores caen al default RedCapital (decision Fase 45).
 *  - Google Maps fue eliminado completo en Fase 46 (Address.vue removido,
 *    no quedaron consumidores de window.google.maps).
 */

const SCRIPT_ID_GTM = "rc-script-gtm";
const SCRIPT_ID_GTM_INIT = "rc-script-gtm-init";
const SCRIPT_ID_HOTJAR = "rc-script-hotjar";

declare global {
  interface Window {
    dataLayer: any[];
    hj: any;
    _hjSettings: any;
  }
}

function injectScript(id: string, src: string, attrs: Record<string, string> = {}): void {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
  document.head.appendChild(script);
}

function injectInlineScript(id: string, code: string): void {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.text = code;
  document.head.appendChild(script);
}

function loadGoogleTagManager(gtmId: string | null | undefined): void {
  if (!gtmId || gtmId.trim().length === 0) return;
  // Init de gtag.js + dataLayer (equivalente a lo que vivia en index.html).
  injectInlineScript(
    SCRIPT_ID_GTM_INIT,
    `window.dataLayer = window.dataLayer || [];` +
      `function gtag(){dataLayer.push(arguments);}` +
      `gtag('js', new Date());` +
      `gtag('config', ${JSON.stringify(gtmId)});`
  );
  injectScript(
    SCRIPT_ID_GTM,
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gtmId)}`,
    { async: "true" }
  );
}

function loadHotjar(hjid: string | null | undefined): void {
  if (!hjid || hjid.trim().length === 0) return;
  if (document.getElementById(SCRIPT_ID_HOTJAR)) return;
  // Snippet oficial de Hotjar (tracking code), traducido del IIFE clasico
  // que vivia hardcoded en public/index.html con hjid:2282064 hsv:6.
  injectInlineScript(
    SCRIPT_ID_HOTJAR,
    `(function(h,o,t,j,a,r){` +
      `h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};` +
      `h._hjSettings={hjid:${JSON.stringify(parseInt(hjid, 10))},hjsv:6};` +
      `a=o.getElementsByTagName('head')[0];` +
      `r=o.createElement('script');r.async=1;` +
      `r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;` +
      `a.appendChild(r);` +
      `})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`
  );
}

export function loadDynamicScripts(theme: ThemeConfig): void {
  if (typeof document === "undefined") return; // SSR safety
  loadGoogleTagManager(theme.gtmId);
  loadHotjar(theme.hotjarId);
}
